import React, { useState } from 'react';
import StatusBadge from '../../components/StatusBadge';
import { HEADER_CELL } from '../../utils/tableColumns';
import { trackingUrl } from '../../utils/trackingUrl';

export const SHIPMENT_COLUMNS =
  'grid-cols-[minmax(100px,2.39fr)_minmax(116px,2.7fr)_minmax(116px,3.12fr)_minmax(116px,2.63fr)_minmax(84px,2.24fr)_88px]';
const DISTRIBUTOR_COLUMNS =
  'grid-cols-[minmax(100px,2.39fr)_minmax(116px,2.7fr)_minmax(116px,3.12fr)_minmax(120px,3fr)_minmax(116px,2.63fr)_minmax(84px,2.24fr)_88px]';

function CopyTracking({ text, t }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      disabled={!text}
      aria-label={t(copied ? 'Copied!' : 'Copy tracking ID')}
      title={t('Copy tracking ID')}
      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 text-[#64748b]"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
        } catch {
          setCopied(false);
        }
      }}
    >
      <span className="material-symbols-outlined text-[16px]">
        {copied ? 'check' : 'content_copy'}
      </span>
    </button>
  );
}

// Same presentation for direct and distributor shipments; callers retain their own actions.
export default function ShipmentListTable({
  shipments,
  distributors = [],
  showDistributor = false,
  t,
  statusForShipment,
  onView,
  renderActions,
}) {
  const columns = showDistributor ? DISTRIBUTOR_COLUMNS : SHIPMENT_COLUMNS;
  return (
    <div
      role="table"
      aria-label={t('Shipments')}
      className={showDistributor ? 'min-w-[900px]' : undefined}
    >
      <div
        role="rowgroup"
        className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px]"
      >
        <div role="row" className={`grid ${columns} px-8 py-4`}>
          {[
            'ORDER ID',
            'SUBMITTED BY',
            'PRACTICE',
            ...(showDistributor ? ['Distributor'] : []),
            'TRACKING ID',
            'STATUS',
            'ACTIONS',
          ].map((col) => (
            <span role="columnheader" key={col} className={HEADER_CELL}>
              {t(col)}
            </span>
          ))}
        </div>
      </div>
      <div role="rowgroup" className="divide-y divide-[#e2e8f0]">
        {!shipments.length && (
          <div role="row" className="px-8 py-8 text-center text-sm text-[#64748b]">
            <span role="cell">{t('No shipments found')}</span>
          </div>
        )}
        {shipments.map((shipment) => {
          const id = shipment.order?.orderId || shipment.orderId || t('N/A');
          const user = shipment.order?.patient;
          const submittedBy = user
            ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || t('N/A')
            : shipment.submittedBy || t('N/A');
          const practice =
            shipment.order?.clinic?.name ||
            shipment.order?.doctor?.department ||
            shipment.practice ||
            '—';
          const organizationId =
            shipment.distributorId?._id ||
            shipment.distributorId ||
            shipment.order?.distributorId?._id ||
            shipment.order?.distributorId;
          const organization =
            distributors.find((row) => row._id === organizationId)?.name ||
            shipment.distributorId?.name ||
            t('Unavailable');
          const url = trackingUrl(shipment.carrier, shipment.trackingNumber);
          return (
            <div
              key={shipment._id}
              role="row"
              className={`grid ${columns} px-8 py-4 items-center hover:bg-gray-50/50 transition-colors`}
            >
              <span role="cell" className="text-xs font-medium text-[#0f172a]">
                {id}
              </span>
              <span
                role="cell"
                className="text-xs font-medium text-[#0f172a] truncate"
                title={submittedBy}
              >
                {submittedBy}
              </span>
              <span
                role="cell"
                className="text-xs font-medium text-[#0f172a] truncate"
                title={practice}
              >
                {practice}
              </span>
              {showDistributor && (
                <span
                  role="cell"
                  className="text-xs font-medium text-[#0f172a] truncate"
                  title={organization}
                >
                  {organization}
                </span>
              )}
              <span role="cell" className="flex items-center gap-2">
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-[#f9fafc] text-xs font-medium text-[#0089ff] underline hover:text-[#0077e6]"
                  >
                    {shipment.trackingNumber}
                  </a>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-[#f9fafc] text-xs font-medium text-[#64748b]">
                    {shipment.trackingNumber || '—'}
                  </span>
                )}
                <CopyTracking text={shipment.trackingNumber} t={t} />
              </span>
              <span role="cell">
                <StatusBadge status={statusForShipment(shipment)} />
              </span>
              <span role="cell" className="flex items-center gap-1">
                {renderActions ? (
                  renderActions(shipment)
                ) : (
                  <button
                    type="button"
                    onClick={() => onView(shipment)}
                    aria-label={t('Shipping documents for order {id}', { id })}
                    title={t('Shipping documents')}
                    className="w-6 h-6 flex items-center justify-center rounded text-[#0089ff] hover:bg-blue-50"
                  >
                    <span className="material-symbols-outlined text-[18px]">description</span>
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
